import React, { useState, useRef, useEffect } from 'react';
import { Button, Flex, theme } from 'antd';
import { PlayCircleFilled, PauseCircleFilled } from '@ant-design/icons';
import { useTheme } from '../../../../../../Shared/GlobalContext/ThemeContext';

const voiceBars = [42, 70, 36, 88, 54, 64, 32, 76, 46, 84, 38, 66, 50, 92, 44, 60, 34, 78, 52, 72, 40, 86, 48, 68];
const VOICE_PROGRESS_COLOR = '#1DA1F2';
const VOICE_PROGRESS_ON_PRIMARY = '#22D3EE';

const AudioMessage = ({ url, duration, sender = 'customer', isCustomerMessage = false }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [totalDuration, setTotalDuration] = useState(typeof duration === 'number' ? duration : 0);
    const waveformRef = useRef(null);
    const audioRef = useRef(null);

    const { token } = theme.useToken();
    const { isDarkMode = false } = useTheme() || {};

    useEffect(() => {
        if (typeof duration === 'number' && duration > 0) {
            setTotalDuration(duration);
            setIsLoading(false);
        }
    }, [duration]);

    const handlePlayPause = (event) => {
        event.stopPropagation();
        if (audioRef.current) {
            if (isPlaying) {
                audioRef.current.pause();
            } else {
                audioRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const handleTimeUpdate = () => {
        if (audioRef.current) {
            const total = audioRef.current?.duration || totalDuration || 0;
            const percentage = total ? (audioRef.current.currentTime / total) * 100 : 0;
            setProgress(percentage);
            setCurrentTime(audioRef.current.currentTime);
        }
    };

    const handleEnded = () => {
        setIsPlaying(false);
        setProgress(0);
        setCurrentTime(0);
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const isOnPrimaryBubble = Boolean(isCustomerMessage);
    const mutedForeground = isOnPrimaryBubble
        ? 'rgba(255, 255, 255, 0.74)'
        : isDarkMode ? 'rgba(255, 255, 255, 0.68)' : token.colorTextSecondary;
    const inactiveBar = isOnPrimaryBubble
        ? 'rgba(255, 255, 255, 0.30)'
        : isDarkMode ? 'rgba(255, 255, 255, 0.22)' : '#d9e1ea';
    const activeBar = isOnPrimaryBubble ? VOICE_PROGRESS_ON_PRIMARY : VOICE_PROGRESS_COLOR;
    const playButtonColor = isOnPrimaryBubble ? token.colorWhite : VOICE_PROGRESS_COLOR;
    const playButtonBackground = isOnPrimaryBubble
        ? 'rgba(255, 255, 255, 0.14)'
        : isDarkMode ? 'rgba(29, 161, 242, 0.16)' : 'rgba(29, 161, 242, 0.10)';

    const containerStyle = {
        padding: isOnPrimaryBubble ? '2px 0' : '6px 8px',
        borderRadius: isOnPrimaryBubble ? 0 : 999,
        backgroundColor: isOnPrimaryBubble ? 'transparent' : isDarkMode ? '#262626' : token.colorBgContainer,
        minWidth: 232,
        maxWidth: '100%',
    };

    const waveformContainerStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: 3,
        height: 32,
        width: 124,
        flex: '0 0 124px',
    };

    const barStyle = (index) => ({
        width: 3,
        height: `${voiceBars[index]}%`,
        backgroundColor: (progress / 100) * voiceBars.length > index
            ? activeBar
            : inactiveBar,
        transition: 'all 0.2s',
        borderRadius: token.borderRadiusXS,
        minWidth: '2px',
        flexShrink: 0,
    });

    const timeStyle = {
        fontSize: token.fontSizeSM,
        color: mutedForeground,
        minWidth: 72,
        textAlign: 'right',
        whiteSpace: 'nowrap',
    };

    return (
        <div style={containerStyle}>
            <Flex gap={2} align="center">
                <Button
                    type="text"
                    title=""
                    aria-label={isPlaying ? 'Pause audio' : 'Play audio'}
                    icon={isPlaying ? (
                        <PauseCircleFilled style={{
                            fontSize: 28,
                            color: playButtonColor
                        }} />
                    ) : (
                        <PlayCircleFilled style={{
                            fontSize: 28,
                            color: playButtonColor
                        }} />
                    )}
                    onClick={handlePlayPause}
                    loading={isLoading}
                    style={{
                        padding: 0,
                        border: 'none',
                        background: playButtonBackground,
                        borderRadius: '50%',
                        width: 32,
                        height: 32,
                        minWidth: 32,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                />

                <div style={waveformContainerStyle} ref={waveformRef}>
                    {voiceBars.map((_, index) => (
                        <div
                            key={index}
                            style={barStyle(index)}
                        />
                    ))}
                </div>

                <span style={timeStyle}>
                    {formatTime(currentTime)} / {formatTime(totalDuration)}
                </span>

                <audio
                    ref={audioRef}
                    src={url}
                    onLoadedMetadata={(e) => {
                        setIsLoading(false);
                        const d = e.currentTarget.duration || 0;
                        if (!totalDuration && d) {
                            setTotalDuration(d);
                        }
                    }}
                    onCanPlay={(e) => {
                        setIsLoading(false);
                        const d = e.currentTarget.duration || 0;
                        if (!totalDuration && d) {
                            setTotalDuration(d);
                        }
                    }}
                    onTimeUpdate={handleTimeUpdate}
                    onEnded={handleEnded}
                    style={{ display: 'none' }}
                />
            </Flex>
        </div>
    );
};

export default AudioMessage;
